/**
 * Ukrainian render vocabulary.
 *
 * Self-registering: importing this module makes the words available to
 * `render(node, 'uk')`. Kept separate from the profile so a parse-only
 * bundle can drop it — see lexicon-registry.ts for why that separation exists.
 */
import type { LanguageLexicon } from '../generators/profiles/types';
import { registerLexicon } from '../lexicon-registry';

export const ukrainianLexicon: LanguageLexicon = {
  events: {
    blur: { primary: 'розфокус' },
    change: { primary: 'зміна' },
    click: { primary: 'клік' },
    dblclick: { primary: 'подвійний_клік' },
    focus: { primary: 'фокус' },
    input: { primary: 'введення' },
    keydown: { primary: 'клавіша_вниз' },
    keypress: { primary: 'натискання_клавіші' },
    keyup: { primary: 'клавіша_вгору' },
    load: { primary: 'завантаження' },
    mousedown: { primary: 'мишавниз' },
    mouseenter: { primary: 'миша_вхід' },
    mouseleave: { primary: 'миша_вихід' },
    mousemove: { primary: 'рух_миші' },
    mouseout: { primary: 'відведення' },
    mouseover: { primary: 'наведення' },
    mouseup: { primary: 'мишавгору' },
    reset: { primary: 'скинути' },
    resize: { primary: 'змінарозміру' },
    scroll: { primary: 'прокрутка' },
    submit: { primary: 'надсилання' },
    touchcancel: { primary: 'дотик_скасування' },
    touchend: { primary: 'дотик_кінець' },
    touchmove: { primary: 'дотик_рух' },
    touchstart: { primary: 'дотик_початок' },
    unload: { primary: 'вивантаження' },
  },
  logical: {
    and: { primary: 'і' },
    changes: { primary: 'змінюється' },
    contains: { primary: 'містить' },
    else: { primary: 'інакше' },
    empty: { primary: 'порожній' },
    end: { primary: 'кінець' },
    equals: { primary: 'дорівнює' },
    exists: { primary: 'існує' },
    has: { primary: 'має' },
    have: { primary: 'маю' },
    includes: { primary: 'включає' },
    is: { primary: 'є' },
    isNot: { primary: 'не_є' },
    live: { primary: 'живий' },
    matches: { primary: 'відповідає' },
    not: { primary: 'не' },
    or: { primary: 'або' },
    otherwise: { primary: 'в_іншому_випадку' },
    then: { primary: 'тоді' },
    when: { primary: 'коли' },
    where: { primary: 'де' },
  },
  temporal: {
    every: { primary: 'кожний' },
    forever: { primary: 'завжди' },
    h: { primary: 'год' },
    hour: { primary: 'година' },
    hours: { primary: 'годин' },
    millisecond: { primary: 'мілісекунда' },
    milliseconds: { primary: 'мілісекунд' },
    min: { primary: 'хв' },
    minute: { primary: 'хвилина' },
    minutes: { primary: 'хвилин' },
    ms: { primary: 'мс' },
    once: { primary: 'один_раз' },
    s: { primary: 'с' },
    second: { primary: 'секунда' },
    seconds: { primary: 'секунд' },
    until: { primary: 'до' },
    times: { primary: 'разів' },
  },
  values: {
    body: { primary: 'тіло' },
    closest: { primary: 'найближчий' },
    detail: { primary: 'деталі' },
    document: { primary: 'документ' },
    element: { primary: 'елемент' },
    event: { primary: 'подія' },
    false: { primary: 'хибність' },
    first: { primary: 'перший' },
    it: { primary: 'це' },
    its: { primary: 'його' },
    last: { primary: 'останній' },
    me: { primary: 'я' },
    my: { primary: 'мій' },
    myself: { primary: 'я_сам' },
    next: { primary: 'наступний' },
    null: { primary: 'нічого' },
    parent: { primary: 'батьківський' },
    previous: { primary: 'попередній' },
    result: { primary: 'результат' },
    target: { primary: 'ціль' },
    true: { primary: 'істина' },
    undefined: { primary: 'невизначено' },
    value: { primary: 'значення' },
    window: { primary: 'вікно' },
    you: { primary: 'ти' },
    your: { primary: 'твій' },
    yourself: { primary: 'сам' },
  },
  attributes: {
    attribute: { primary: 'атрибут' },
    attributes: { primary: 'атрибути' },
    checked: { primary: 'позначено' },
    class: { primary: 'клас' },
    classes: { primary: 'класи' },
    disabled: { primary: 'вимкнено' },
    hidden: { primary: 'приховано' },
    html: { primary: 'html' },
    properties: { primary: 'властивості' },
    property: { primary: 'властивість' },
    readonly: { primary: 'лише_читання' },
    required: { primary: "обов'язково" },
    selected: { primary: 'вибрано' },
    style: { primary: 'стиль' },
    styles: { primary: 'стилі' },
    text: { primary: 'текст' },
  },
  expressions: {
    up: { primary: 'вгору' },
    down: { primary: 'вниз' },
    left: { primary: 'вліво' },
    right: { primary: 'вправо' },
    at: { primary: 'в' },
    back: { primary: 'назад' },
    characters: { primary: 'символи' },
    children: { primary: 'діти' },
    closest: { primary: 'найближчий' },
    elsewhere: { primary: 'ззовні' },
    empty: { primary: 'порожній' },
    'ends with': { primary: 'закінчується_на' },
    exclusive: { primary: 'виключно' },
    first: { primary: 'перший' },
    'ignoring case': { primary: 'без_урахування_регістру' },
    inclusive: { primary: 'включно' },
    index: { primary: 'індекс' },
    'joined by': { primary: "об'єднано_через" },
    last: { primary: 'останній' },
    'mapped to': { primary: 'перетворено_на' },
    next: { primary: 'наступний' },
    no: { primary: 'ні' },
    parent: { primary: 'батьківський' },
    prev: { primary: 'попер' },
    previous: { primary: 'попередній' },
    random: { primary: 'випадковий' },
    some: { primary: 'деякі' },
    'sorted by': { primary: 'відсортовано_за' },
    'split by': { primary: 'розділено_за' },
    start: { primary: 'почати' },
    catch: { primary: 'зловити' },
    finally: { primary: 'нарешті' },
    def: { primary: 'функція' },
    'starts with': { primary: 'починається_з' },
    within: { primary: 'всередині' },
  },
};

registerLexicon('uk', ukrainianLexicon);
